import { removeDraftBuffers, renameDraftBuffer } from '@/stores/draftSessionStorage';
import { valueSnapshot } from '@/helpers/valueSnapshot';
import { draftKeyCovers, rekeySlot, type DraftKey } from '@/stores/draftKey';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';

/**
 * Where a create flow's entered values live while its panel is not mounted
 * (TM-9455 / ADR-0029 §B-bis, §H-2).
 *
 * Keys are {@link DraftKey}, not `SidebarType`. A type-keyed store can hold one
 * draft per type; a page that may hold several create panels of the SAME type
 * (ADR-0029 §G) needs one entry per panel instead — the active create column passes
 * its bare type, a parked one a per-instance key (`createPatient#3`), an identified
 * one its own identity. This layer knows only that all three are keys, so it holds
 * them with no branch in it. What a key can BE, and how one comes apart again, lives
 * in `draftKey` and nowhere else.
 *
 * The panels this serves are 200–1500 lines of form each, and lifting their state
 * into typed store fields was never the job. Instead every panel keeps its own
 * declarations exactly as written and hands **ownership** of them here: it passes
 * a factory for its own defaults, and gets the same object back on every remount.
 * All of its existing `state.x` reads keep working untouched.
 *
 * Opaque for a second reason: several of those shapes are declared under
 * `components/`, and the boundaries rule forbids a store reaching in there.
 * Staying generic keeps the dependency pointing the right way.
 *
 * "Buffer", not "slot" (§H-2): "slot" already means a cascade parent-and-source-list
 * slot (ADR-0027) and an appointment time slot in the Slot-Finder, and the draft
 * meaning was the youngest of the three.
 */
export const useDraftStateStore = defineStore('draftState', () => {
  /**
   * Buffer contents, keyed `<key>` or `<key>/<variant>`. `shallowRef` around a plain
   * `Map`: only the CONTENTS need to be reactive (the panel builds them with
   * `reactive`/`ref`), and deep-reactifying the map would auto-unwrap the refs
   * inside each buffer on read, silently breaking every `.value` in the panels.
   */
  const buffers = shallowRef(new Map<DraftKey, object>());

  /**
   * Per BUFFER: what counts as user input, and which draft that buffer belongs to.
   *
   * Registered per variant rather than per draft (§H-1), because two components can
   * share one draft key — the Verordnung's Fall picker and the payer body under it —
   * and a single projection per key meant whichever mounted last silently replaced
   * the other's. `owner` is the draft the buffer hangs off, so {@link dirtyKeys}
   * answers about the DRAFT while the projections stay per buffer: an Entwurf is
   * dirty when any of its buffers differs from its baseline.
   *
   * The projection is registered by the panel but CLOSES OVER the store-held
   * buffer rather than component state, so it keeps answering after the panel
   * unmounts — which is what the menu dot and the parked rail need to ask about
   * a draft on a page they are not on.
   */
  const reads = shallowRef(new Map<DraftKey, { owner: DraftKey; read: () => unknown }>());
  // Stored under the plain string form: a `Record` keyed by the brand becomes an
  // index signature TS will not let anything read, and the brand's job is done at
  // the parameters — nothing can reach these entries without a key in hand.
  const baselines = ref<Record<string, string>>({});

  /**
   * Declare what counts as user input in one buffer.
   *
   * `bufferKey` is the buffer's own key (variant suffix and all); `owner` is the
   * draft it belongs to, and is what dirtiness is reported under. They are the same
   * string for a draft that keeps a single buffer, which is all of them but the
   * Verordnung.
   */
  function track(bufferKey: DraftKey, read: () => unknown, owner: DraftKey = bufferKey): void {
    // The map is REPLACED, never mutated in place. `dirtyKeys` is read during the
    // column's own setup — before the panel inside it has registered anything — so
    // the getter's first evaluation sees an empty map and depends on nothing else.
    // An in-place `set` would not invalidate that, and the answer would stay
    // "nothing is dirty" for the rest of the session no matter what the user typed.
    const next = new Map(reads.value);
    next.set(bufferKey, { owner, read });
    reads.value = next;
  }

  /**
   * Treat one buffer's current values as untouched. Call it after any write the USER
   * did not make — seeding a record, a prefill from an ancestor column, a first blank
   * row. Counting those makes a panel open already dirty and offer to discard changes
   * nobody made.
   *
   * Names ONE buffer, deliberately not a key family. A draft with several buffers has
   * one component per buffer, and each of them seeds on its own schedule: re-baselining
   * a sibling here would declare that component's user's work untouched, and its half of
   * the Entwurf would silently stop counting. `reset` and `rename` walk the family
   * because discarding and graduating are decisions about the whole Entwurf; seeding
   * never is.
   */
  function rebaseline(bufferKey: DraftKey): void {
    const entry = reads.value.get(bufferKey);
    if (!entry) return;
    baselines.value = { ...baselines.value, [bufferKey]: valueSnapshot(entry.read()) };
  }

  /**
   * The DRAFTS holding values that differ from their baseline.
   *
   * A getter rather than an `isDirty(key)` method on purpose: `createTestingPinia`
   * stubs store ACTIONS to return undefined, so a method would silently report
   * "not dirty" in every test that uses the testing plugin — including tests of the
   * badge and the menu dot, the two things that read it.
   *
   * Reported under the OWNER, not the buffer: a draft with several buffers is one
   * Entwurf with one rail and one ✕, so any buffer differing makes the draft dirty.
   */
  const dirtyKeys = computed<ReadonlySet<DraftKey>>(() => {
    const dirty = new Set<DraftKey>();
    for (const [bufferKey, entry] of reads.value) {
      const baseline = baselines.value[bufferKey];
      if (baseline !== undefined && valueSnapshot(entry.read()) !== baseline) {
        dirty.add(entry.owner);
      }
    }
    return dirty;
  });

  /**
   * Move a draft's whole bookkeeping from one key to another, keeping its values.
   *
   * This is the graduation of the ACTIVE create column into the draft zone
   * (ADR-0029 §G): it enters life keyed by its bare type — there is only ever one
   * of those at a time — and takes an instance key when it moves right to make
   * room for the next one.
   *
   * Nothing is copied. The buffer OBJECT is handed over untouched, which is what
   * makes the move invisible to the panel: its registered projection closes over
   * that object rather than over the key, so it keeps reporting the same values
   * before, during and after. The baseline travels with it, so dirtiness never
   * blinks off mid-move and no rail or menu dot flickers.
   *
   * A no-op when `from` holds nothing, so a caller need not check first.
   */
  function rename(from: DraftKey, to: DraftKey): void {
    if (from === to) return;
    // Collect first, mutate second. Unlike `reset` — which only deletes, and so can
    // iterate live — this INSERTS keys while walking, and a live iterator would then
    // walk into what it just wrote (`rename('a', 'a/b')` would move the same buffer
    // forever).
    const moves: [DraftKey, DraftKey, object][] = [];
    for (const bufferKey of buffers.value.keys()) {
      const moved = rekeySlot(bufferKey, from, to);
      if (moved) moves.push([bufferKey, moved, buffers.value.get(bufferKey)!]);
    }
    for (const [bufferKey, movedKey, moved] of moves) {
      buffers.value.delete(bufferKey);
      buffers.value.set(movedKey, moved);
      // A reload copy follows its buffer (TM-9664), or the next create would wake up
      // holding the work that just graduated.
      renameDraftBuffer(bufferKey, movedKey);
    }
    // Projections and baselines travel with their buffer, variant suffix and all —
    // the whole family moves at once, which is what keeps a Verordnung's payer body
    // reporting under the same draft after its column has graduated.
    const nextReads = new Map(reads.value);
    const nextBaselines = { ...baselines.value };
    let touchedReads = false;
    let touchedBaselines = false;
    for (const [bufferKey, entry] of reads.value) {
      const movedKey = rekeySlot(bufferKey, from, to);
      if (!movedKey) continue;
      nextReads.delete(bufferKey);
      nextReads.set(movedKey, {
        owner: rekeySlot(entry.owner, from, to) ?? entry.owner,
        read: entry.read,
      });
      touchedReads = true;
      const baseline = baselines.value[bufferKey];
      if (baseline !== undefined) {
        delete nextBaselines[bufferKey];
        nextBaselines[movedKey] = baseline;
        touchedBaselines = true;
      }
    }
    if (touchedReads) reads.value = nextReads;
    if (touchedBaselines) baselines.value = nextBaselines;
  }

  /** Throw one key's draft away: its buffers, its projections and its baselines. */
  function reset(key: DraftKey): void {
    // The reload copies go with the Entwurf (TM-9664).
    removeDraftBuffers(key);
    // Deleting the current entry while iterating a Map is well-defined, so no
    // snapshot of the keys is needed.
    for (const bufferKey of buffers.value.keys()) {
      if (draftKeyCovers(key, bufferKey)) buffers.value.delete(bufferKey);
    }
    const remaining = new Map(reads.value);
    const next = { ...baselines.value };
    for (const bufferKey of reads.value.keys()) {
      if (!draftKeyCovers(key, bufferKey)) continue;
      remaining.delete(bufferKey);
      delete next[bufferKey];
    }
    reads.value = remaining;
    baselines.value = next;
  }

  return {
    buffers,
    track,
    rebaseline,
    dirtyKeys,
    rename,
    reset,
  };
});
